import React, { createRef } from "react";

import BaseElem from "../BaseElem";
import "./DateTimeDisplay.css";
import "../Common.css";
import { InputField } from "./InputField";
import Calendar from "./CalendarView/Calendar";
import { UNDEF } from "../Constants";
import {
  minsToAMPM,
  areDatesSame,
  dateAdd,
  getDurationString,
  isPastDate,
  isFutureDate,
  convertToDestTZ,
  destOffsetInMillis,
} from "../Util/DateUtils";
import { guessMinutes, guessDate } from "Util/Utilities";
import { Div } from "./GenericView";

type DateTimeDisplayProps = {
  vertical?: boolean;
  startDate?: Date;
  endDate?: Date;
  onChange: Function;

  singleDateMode?: boolean;
  dontAllowPastDates?: boolean;
  dontAllowFutureDates?: boolean;
  region?: string;

  // disable background and size animation
  dontAnimate?: boolean;

  // do not show today indicator
  dontHighlightToday?: boolean;
  boldToday?: boolean;

  style?: any;
  className?: string;
  placeholder?: string; // placeholder for startDate
  inputClassName?: string;
  lineHeight?: number;

  // disable all inputs for readonly state
  readOnly?: boolean;
  updating?: boolean;
};

type DateTimeDisplayState = {
  startTimeList: number[];
  endDateError: boolean;
  _haveFocus: boolean;
};

export function getDuration(d1: any, d2: any) {
  const str = getDurationString(d1, d2, true);
  // for now visits are only 24hrs max
  const plus = d1 && d2 && d2.getDate() !== d1.getDate() ? "+1" : "";
  return str.length ? `${plus}(${str})` : str;
}

export class DateTimeDisplay extends BaseElem<
  DateTimeDisplayProps,
  DateTimeDisplayState
> {
  refDF1 = createRef<InputField>();
  refDF2 = createRef<InputField>();
  refTF1 = createRef<InputField>();
  refTF2 = createRef<InputField>();
  refSTDropdown = createRef<HTMLDivElement>();
  refETDropdown = createRef<HTMLDivElement>();

  constructor(props: DateTimeDisplayProps) {
    super(props);

    const startTimes: number[] = [];
    for (let i = 0; i < 96; i++) {
      startTimes.push(i * 15);
    }

    this.state = {
      startTimeList: startTimes,
      endDateError: false,
      _haveFocus: false,
    };
  }

  _generateEndTimeList(startDate?: Date, endDate?: Date): number[] {
    const self = this;
    const endTimes: number[] = [];
    const after30Mins = 30; // minimum duration 30 mins (for speech therapy)

    if (startDate && endDate && areDatesSame(startDate, endDate)) {
      const startTimeMins = startDate.getHours() * 60 + startDate.getMinutes();
      for (let i = 0; i < 96; i++) {
        endTimes.push(startTimeMins + after30Mins + i * 15);
      }
    } else {
      endTimes.push(...self.state.startTimeList);
    }

    return endTimes;
  }

  _setFocus(haveFocus = true) {
    this.setState({ _haveFocus: haveFocus });
    return true;
  }
  _focus = () => {
    this._setFocus(true);
  };
  _focusAndScroll = (comp: any) => {
    this._setFocus(true);
    this._asyncScroll(comp);
  };
  _unfocus = () => {
    this._setFocus(false);
  };

  _dropdownItemClick(isStartTime: boolean, minutes: number, blur = true): Date {
    const self = this,
      props = self.props;
    let startDate = props.startDate;
    let endDate = props.endDate;

    const militaryHours = Math.floor(minutes / 60);
    const h = militaryHours; // % 24;
    const m = minutes - militaryHours * 60;
    let updatedDate = new Date(
      isStartTime ? startDate || new Date() : endDate!
    );
    updatedDate.setHours(h);
    updatedDate.setMinutes(m);
    if (this.props.updating) {
      // update case: when needs to minus offset from location time
      updatedDate = new Date(
        updatedDate.getTime() - destOffsetInMillis(this.props.region)
      );
    }

    if (isStartTime) {
      startDate = updatedDate;
    } else {
      endDate = updatedDate;
    }

    const ret = props.onChange(
      isStartTime ? updatedDate : UNDEF,
      isStartTime ? UNDEF : updatedDate
    );

    startDate = ret.startDate;
    endDate = ret.endDate;

    const hasError =
      startDate && endDate && endDate.getTime() <= startDate.getTime();
    endDate && self.setState({ endDateError: !!hasError });

    blur && self._blurFields();
    return updatedDate;
  }

  // Date changed using calendar
  //  copy the time value and notify outside with callback
  _notifyDateChange(
    isStartDate: boolean,
    date?: Date,
    blur = true
  ): Date | undefined {
    const self = this,
      props = self.props;
    let startDate = props.startDate;
    let endDate = props.endDate;

    date && (date = new Date(date)); // make copy
    let ret;

    if (isStartDate) {
      if (startDate && date) {
        date.setHours(startDate.getHours());
        date.setMinutes(startDate.getMinutes());
      }

      startDate = date;
      ret = props.onChange(date);

      if (!props.singleDateMode) {
        // auto select end date as well
        endDate = self._notifyDateChange(false, date, blur);
      }
    } else if (!props.singleDateMode && date) {
      date.setHours(endDate!.getHours());
      date.setMinutes(endDate!.getMinutes());
      endDate = date;
      ret = props.onChange(UNDEF, date);
    }

    startDate = ret && ret.startDate;
    endDate = ret && ret.endDate;

    const hasError =
      startDate && endDate && endDate.getTime() <= startDate.getTime();
    endDate && self.setState({ endDateError: !!hasError });

    blur && self._blurFields();
    return date;
  }

  _blurFields() {
    const self = this;
    [self.refDF1, self.refDF2, self.refTF1, self.refTF2].forEach((e) =>
      e.current?.blur()
    );
  }

  _onTextChange = (comp: InputField, value: string) => {
    const self = this;
    const props = self.props;
    const tf1 = self.refTF1.current;
    const tf2 = self.refTF2.current;

    if (comp === tf1 || comp === tf2) {
      const minutes = guessMinutes(value);
      if (minutes === UNDEF) return;

      self._dropdownItemClick(comp === tf1, minutes, false);
      self._asyncScroll(comp, 100);
    } else {
      // handle date inputs
      const isStartDate = comp === self.refDF1.current;
      let date;

      if (!value && props.singleDateMode) {
        date = UNDEF;
      } else {
        date = guessDate(value, isStartDate ? props.startDate : props.endDate);
        if (date === UNDEF || (props.dontAllowPastDates && isPastDate(date))) {
          return;
        }
      }

      self._notifyDateChange(isStartDate, date, false);
    }
  };

  // change dates with up/down arrows on calendar
  _onKeyPress = (comp: InputField, key: string, e: any) => {
    const isUpArrow = key === "ArrowUp";
    const isDownArrow = key === "ArrowDown";

    if (!isUpArrow && !isDownArrow) {
      return;
    }
    e.preventDefault();

    const self = this;
    const props = self.props;
    const dontAllowPastDates = props.dontAllowPastDates;
    const dontAllowFutureDates = props.dontAllowFutureDates;

    if (props.readOnly) return;

    const offset = isDownArrow ? 1 : -1;
    let updatedValue: Date | undefined;

    if (comp === self.refDF1.current) {
      const notifDate = !props.startDate
        ? new Date()
        : dateAdd(props.startDate, offset)!;
      if (dontAllowPastDates && isPastDate(notifDate)) return;
      if (dontAllowFutureDates && isFutureDate(notifDate)) return;

      updatedValue = self._notifyDateChange(true, notifDate, false);
    } else if (comp === self.refDF2.current) {
      const updatedDate = dateAdd(props.endDate, offset)!;
      if (dontAllowPastDates && isPastDate(updatedDate)) return;
      if (dontAllowFutureDates && isFutureDate(updatedDate)) return;

      updatedValue = self._notifyDateChange(false, updatedDate, false);
    }

    const isStartTime = comp === self.refTF1.current;
    const isEndTime = comp === self.refTF2.current;

    if (isStartTime) {
      let idx = self._getSelectedMinutesIndex(isStartTime) + offset;
      const startTimes = self.state.startTimeList;
      idx = idx < 0 ? startTimes.length - 1 : idx % startTimes.length;
      updatedValue = self._dropdownItemClick(
        isStartTime,
        startTimes[idx],
        false
      );
      self._scrollDropdownToSelectedItem(comp, 0);
    } else if (isEndTime) {
      let idx = self._getSelectedMinutesIndex(isStartTime) + offset;
      const endTimes = self._generateEndTimeList(
        self.props.startDate,
        self.props.endDate!
      );
      idx = idx < 0 ? endTimes.length - 1 : idx % endTimes.length;
      updatedValue = self._dropdownItemClick(isStartTime, endTimes[idx], false);
      self._scrollDropdownToSelectedItem(comp, 0);
    }

    comp.updateValue(updatedValue);
  };

  _getSelectedMinutes(isStartTime: boolean): number {
    // handling create and update visit with these condition
    const date =
      this.props.updating || this.props.readOnly
        ? convertToDestTZ(
            this.props[isStartTime ? "startDate" : "endDate"],
            this.props.region
          )
        : this.props[isStartTime ? "startDate" : "endDate"];
    return !date ? -1 : date.getHours() * 60 + date.getMinutes();
  }
  _getSelectedMinutesIndex(isStartTime: boolean): number {
    const self = this,
      state = this.state;
    const selectedMinutes = self._getSelectedMinutes(isStartTime);
    if (isStartTime) {
      return state.startTimeList.indexOf(selectedMinutes);
    } else {
      return self
        ._generateEndTimeList(self.props.startDate, self.props.endDate!)
        .findIndex((x) => x % 1440 === selectedMinutes);
    }
  }

  _isTimeSelected(isStartTime: boolean, minutes: number): boolean {
    return this._getSelectedMinutes(isStartTime) === minutes % 1440;
  }

  _asyncScroll(comp: any, delay = 100) {
    this.async(() => this._scrollDropdownToSelectedItem(comp, delay), 1);
  }

  _scrollDropdownToSelectedItem(comp: any, delay = 100) {
    const self = this;
    const isStartTime = comp === self.refTF1.current;
    const isEndTime = comp === self.refTF2.current;
    if ((!isStartTime && !isEndTime) || self.props.readOnly) return;

    const ITEM_HEIGHT = 40; //40px hardcoded in css
    const selectedIndex = self._getSelectedMinutesIndex(isStartTime);
    const dropdown = (isStartTime ? self.refSTDropdown : self.refETDropdown)
      .current;
    dropdown &&
      selectedIndex >= 0 &&
      self.async(() => {
        dropdown.scrollTop = Math.max(0, ITEM_HEIGHT * (selectedIndex - 2)); //-2 is to center
      }, delay);
  }

  render() {
    const self = this,
      state = self.state,
      props = self.props;
    const region = this.props.region;
    return (
      <div
        className={
          `dtd ${props.className || ""} ` + (props.vertical ? "v" : "h")
        }
        style={props.style}
      >
        <InputField
          ref={self.refDF1}
          type="date"
          region={region}
          date={props.startDate}
          blurOnTabKey
          blurOnEnterKey
          className={props.inputClassName}
          lineHeight={props.lineHeight}
          dontAnimate={props.dontAnimate}
          placeholder={props.placeholder}
          isBold={props.vertical}
          readOnly={props.readOnly}
          popupDisabled={props.readOnly}
          onFocus={self._focus}
          onBlur={self._unfocus}
          onKeyPress={self._onKeyPress}
          onTextChange={self._onTextChange}
        >
          {!props.readOnly && state._haveFocus && (
            <Calendar
              selectedDate={props.startDate}
              dontAllowPastDates={props.dontAllowPastDates}
              dontAllowFutureDates={props.dontAllowFutureDates}
              dontHighlightToday={props.dontHighlightToday}
              boldToday={props.boldToday}
              onDateSelected={(d: Date) => self._notifyDateChange(true, d)}
            />
          )}
        </InputField>

        {!props.singleDateMode && (
          <div className="h center">
            <InputField
              ref={self.refTF1}
              region={region}
              type="time"
              date={props.startDate}
              blurOnTabKey
              blurOnEnterKey
              popupHeight="200px"
              updating={props.updating}
              className={props.inputClassName}
              lineHeight={props.lineHeight}
              dontAnimate={props.dontAnimate}
              readOnly={props.readOnly}
              popupDisabled={props.readOnly}
              onKeyPress={self._onKeyPress}
              onFocus={self._focusAndScroll}
              onBlur={self._unfocus}
              onTextChange={self._onTextChange}
            >
              {!props.readOnly && state._haveFocus && (
                <div
                  className="dropdown-list dtd-list"
                  ref={self.refSTDropdown}
                >
                  {state.startTimeList.map((t: any) => (
                    <div
                      key={t}
                      className="dropdown-item dtd-itm"
                      onClick={(_) => self._dropdownItemClick(true, t)}
                      item-selected={self._isTimeSelected(true, t) ? "" : UNDEF}
                    >
                      {minsToAMPM(t, false)}
                    </div>
                  ))}
                </div>
              )}
            </InputField>

            <Div pv={2} />

            <InputField
              ref={self.refTF2}
              type="time"
              region={region}
              date={props.endDate}
              blurOnTabKey
              updating={props.updating}
              blurOnEnterKey
              className={props.inputClassName}
              lineHeight={props.lineHeight}
              dontAnimate={props.dontAnimate}
              readOnly={props.readOnly}
              popupDisabled={props.readOnly}
              onKeyPress={self._onKeyPress}
              onFocus={self._focusAndScroll}
              onBlur={self._unfocus}
              onTextChange={self._onTextChange}
              popupHeight="200px"
              hasError={state.endDateError}
            >
              {!props.readOnly && state._haveFocus && (
                <div
                  className="dropdown-list dtd-list"
                  ref={self.refETDropdown}
                >
                  {self
                    ._generateEndTimeList(props.startDate, props.endDate!)
                    .map((t: any) => (
                      <div
                        key={t}
                        className="dropdown-item dtd-itm"
                        onClick={(_) => self._dropdownItemClick(false, t)}
                        item-selected={
                          self._isTimeSelected(false, t) ? "" : UNDEF
                        }
                      >
                        {minsToAMPM(t, false)}
                      </div>
                    ))}
                </div>
              )}
            </InputField>

            {props.vertical && !props.singleDateMode && (
              <div className="dtd-duration">
                {getDuration(props.startDate, props.endDate)}
              </div>
            )}
          </div>
        )}

        {!props.singleDateMode && <div className="dtd-v-divider" />}

        {!props.singleDateMode && (
          <InputField
            ref={self.refDF2}
            type="date"
            date={
              props.updating || props.readOnly
                ? convertToDestTZ(props.endDate, region)
                : props.endDate
            }
            region={region}
            blurOnTabKey
            blurOnEnterKey
            className={props.inputClassName}
            lineHeight={props.lineHeight}
            dontAnimate={props.dontAnimate}
            readOnly={props.readOnly}
            popupDisabled={props.readOnly}
            onFocus={self._focus}
            onBlur={self._unfocus}
            onKeyPress={self._onKeyPress}
            onTextChange={self._onTextChange}
            hasError={state.endDateError}
          >
            {!props.readOnly && state._haveFocus && (
              <Calendar
                selectedDate={props.endDate}
                dontAllowPastDates={props.dontAllowPastDates}
                dontAllowFutureDates={props.dontAllowFutureDates}
                dontHighlightToday={props.dontHighlightToday}
                boldToday={props.boldToday}
                onDateSelected={(d: Date) => self._notifyDateChange(false, d)}
              />
            )}
          </InputField>
        )}

        {!props.vertical && !props.singleDateMode && (
          <div className="dtd-duration as-center">
            {getDuration(props.startDate, props.endDate)}
          </div>
        )}
      </div>
    );
  }
}
